import { useLatest } from 'ahooks';
import { cloneDeep, noop } from 'lodash';
import React, { memo, useEffect } from 'react';
import { BaseLayerView, getFramework } from '@/framework';
import { arePropsEqual, COL_ELEMENT_IS_BLOCK } from '@/utils/common-kit';
import { ILayerProps, IRenderLayerProps, ITableLayer, ITableLayerProps } from '@/version-types';
import { getFixedTableNumber } from '../utils/fix-number';
import { getUpdatedTableStyle } from '../utils/table';
import Borders from './borders';
import CellComponent from './cell-component';

export interface TableLoadingParams {
    /** 只展示loading，不展示表格相关的内容与样式 (只是通过TableNode组件起到布局作用) */
    onlyShowLoading?: boolean;
    loadingNode?: (cellId: string) => React.ReactNode;
}

interface TableNodeProps {
    isInEditorLayer?: boolean;
    tableLayerWidth: number;
    tableLayerData: ITableLayerProps;
    fontFamilyMap: Record<string, string>;
    tableLoadingParams?: TableLoadingParams;
}

export const TableNode: React.FC<TableNodeProps> = memo(
    ({ isInEditorLayer, tableLayerWidth, tableLayerData, fontFamilyMap, tableLoadingParams }) => {
        const { baseCells, cells: cellsProp } = tableLayerData;

        const getCellHeightCss = (cellHeight: number) => ({
            '--cell-height': `${cellHeight}px`,
        });

        const tableNode = (
            <table
                style={{
                    width: tableLayerWidth,
                    tableLayout: 'fixed',
                    wordBreak: 'break-all',
                    borderCollapse: 'collapse',
                }}
            >
                <colgroup>
                    {baseCells.horizontal.map((hSize, hIndex) => (
                        <col key={`col-${hIndex}`} style={{ width: hSize }} />
                    ))}
                </colgroup>

                <tbody>
                    {baseCells.vertical.map((_, vIndex) => (
                        <tr
                            key={`tr-${vIndex}`}
                            style={{
                                height: baseCells.vertical[vIndex],
                                ...getCellHeightCss(baseCells.vertical[vIndex]),
                            }}
                        >
                            <TrInnerNode
                                tableLayerData={tableLayerData}
                                fontFamilyMap={fontFamilyMap}
                                isInEditorLayer={isInEditorLayer}
                                cellsProp={cellsProp}
                                vIndex={vIndex}
                                tableLoadingParams={tableLoadingParams}
                            />
                        </tr>
                    ))}
                </tbody>
            </table>
        );
        return tableNode;
    },
);

TableNode.displayName = 'TableNode';

const TrInnerNode = memo(
    ({
        isInEditorLayer,
        tableLayerData,
        fontFamilyMap,
        cellsProp,
        vIndex,
        tableLoadingParams,
    }: {
        isInEditorLayer?: boolean;
        tableLayerData: Pick<ITableLayerProps, 'allStyleId' | 'styles' | 'presetId'>;
        fontFamilyMap: Record<string, string>;
        cellsProp: ITableLayerProps['cells'];
        vIndex: number;
        tableLoadingParams?: TableLoadingParams;
    }) => (
        <>
            {!cellsProp[vIndex]
                ? null
                : cellsProp[vIndex].map((cell, hIndex) => {
                      /** 占位dom, 目的是为了方便在表格中查询某行某列对应的dom */
                      if (!cell) return <td key={`td-${vIndex}-${hIndex}`} style={{ display: 'none' }}></td>;
                      return (
                          <CellComponent
                              key={cell.__renderId}
                              cell={cell}
                              tableLayerData={tableLayerData}
                              fontFamilyMap={fontFamilyMap}
                              isInEditorLayer={isInEditorLayer}
                              tableLoadingParams={tableLoadingParams}
                          />
                      );
                  })}
        </>
    ),
    (preProps, nextProps) => {
        return (
            arePropsEqual(preProps, nextProps, ['tableLayerData', 'cellIdsOfLoadingFont', 'fontFamilyMap']) &&
            preProps.tableLayerData.allStyleId === nextProps.tableLayerData.allStyleId &&
            preProps.tableLayerData.presetId === nextProps.tableLayerData.presetId &&
            JSON.stringify(preProps.fontFamilyMap) === JSON.stringify(nextProps.fontFamilyMap) &&
            JSON.stringify(preProps.tableLayerData.styles) === JSON.stringify(nextProps.tableLayerData.styles) &&
            preProps.tableLoadingParams === nextProps.tableLoadingParams
        );
    },
);

TrInnerNode.displayName = 'TrInnerNode';

/**
 * 修复表格, 比如设置行列数时, 在部分浏览器上无法直接计算表格布局, 需要在运行时通过dom计算的方式去修复
 * 注: 用过InputNumber的滚动模式去设置行列数会非常频繁, 所以不能创建dom去计算, 直接拿运行时的dom才能优化性能
 */
const useFixTableLayout = ({
    frameworkIdentity,
    tableLayer,
    isNavigator,
}: {
    frameworkIdentity: string;
    tableLayer: ITableLayer;
    isNavigator?: boolean;
}) => {
    const tableLayerRef = useLatest(tableLayer);
    const { shouldFixLayout } = tableLayer.__runtime ?? {};

    useEffect(() => {
        if (isNavigator) return;
        if (!shouldFixLayout) return;

        const tableLayer = tableLayerRef.current!;

        const newLayout = getUpdatedTableStyle({
            frameworkIdentity,
            tableLayerId: tableLayer.id,
            tableRotate: tableLayer.rotate,
            action: noop,
            compute: (_, { getLayout }) => getLayout(),
        })!;
        console.log('===表格自动修复', {
            oldLayout: {
                size: `w: ${tableLayer.width}; h: ${tableLayer.height}`,
                hSies: tableLayer.data.baseCells.horizontal,
                vSies: tableLayer.data.baseCells.vertical,
            },
            newLayout: {
                size: `w: ${newLayout.tableWidth}; h: ${newLayout.tableHeight}`,
                hSies: newLayout.baseCells.horizontal,
                vSies: newLayout.baseCells.vertical,
            },
        });
        let newTableLayer = cloneDeep(tableLayerRef.current)!;

        if (COL_ELEMENT_IS_BLOCK) {
            newTableLayer.data.baseCells.horizontal = newLayout.baseCells.horizontal;
        }
        newTableLayer.data.baseCells.vertical = newLayout.baseCells.vertical;
        newTableLayer.width = newLayout.tableWidth;
        newTableLayer.height = newLayout.tableHeight;
        newTableLayer = getFixedTableNumber(newTableLayer);
        delete newTableLayer.__runtime?.shouldFixLayout;

        setTimeout(() => {
            getFramework(frameworkIdentity).stage.addOrUpdate(newTableLayer);
        }, 0);
    }, [isNavigator, shouldFixLayout, frameworkIdentity]);
};

interface IRenderLayerPropsOfTable extends Omit<IRenderLayerProps, 'layer'> {
    // 由于IRenderLayerProps没做范型，导致类型不明确，这里做了适配，明确了具体的类型
    layer: ITableLayer;
}

export const SAAS_EDITOR_TABLE_CLASS_NAME = 'saas-editor__table';

export const InnerTableLayer: React.FC<{
    layer: ITableLayer;
    isNavigator?: boolean;
}> = ({ layer, isNavigator }) => (
    <div
        className={SAAS_EDITOR_TABLE_CLASS_NAME}
        style={{
            position: 'relative',
            height: '100%',
            boxSizing: 'border-box',
        }}
    >
        <div style={{ opacity: layer.opacity }}>
            <Borders tableWidth={layer.width} tableHeight={layer.height} tableLayerData={layer.data} />

            <TableNode
                isInEditorLayer={!isNavigator}
                tableLayerWidth={layer.width}
                tableLayerData={layer.data}
                fontFamilyMap={layer.__runtime?.fontFamilyMap ?? {}}
            />
        </div>
    </div>
);

InnerTableLayer.displayName = 'InnerTableLayer';

/**
 * `table` 结构图: https://xiuxiu-pro-pre.meitudata.com/posters/45278f3450f7d3715ba97c4700dc4f40.png
 *
 * `table` 元素兼容的坑(已解决):
 *
 *  1. Safari 下, `col` 元素宽高为 `0`;
 *  2. Firefox 下, `td` 元素设置 `height: 100%`, 实际上 `height` 直接跟随 `tr` 高度了, 如果 `tr` 元素的 `rowSpan` 大于 1, 那高度就会有 Bug, 所以不能 `td` 元素设置 `height: 100%;`
 *
 * tableLayer存在的精度问题(无法解决):
 *
 *  - 不能对数据进行取整, 因为画布缩放后 DOM 宽高依旧是小数, 不能解决精度问题;
 *  - 不能通过 `stageScale` 去计算 DOM 上应该展示的宽高值, 因为 `table.width / height` 是通过拿 DOM 宽高计算的, 这样会导致 `table.width / height` 与实际数据差几像素, 逻辑无法闭环
 *      1. 精度问题导致 `cellInner` 展示错乱: https://jira.meitu.com/browse/MTXXPRO-41264
 *      2. 精度问题导致表格边框有缝隙: https://jira.meitu.com/browse/MTXXPRO-50922
 *
 * TODO: safari文字宽度比chrome大，导致表格布局错乱: https://pre.designkit.com/s/?sk=2UBZHZ&f=s&c=s&et=pe#我分享了【未命名的设计】，点击查看
 */
const TableLayer: React.FC<IRenderLayerPropsOfTable> = ({ identity: frameworkIdentity, layer, isNavigator }) => {
    useFixTableLayout({
        frameworkIdentity,
        tableLayer: layer,
        isNavigator,
    });

    return (
        <BaseLayerView layer={layer as ILayerProps} isNavigator={isNavigator}>
            <InnerTableLayer layer={layer} isNavigator={isNavigator} />
        </BaseLayerView>
    );
};

TableLayer.displayName = 'TableLayer';

export default memo(TableLayer);
